import React from 'react';
import { Box, Typography, LinearProgress, Link } from '@mui/material';
import { TableChart as ViewIcon } from '@mui/icons-material';
import { ViewAccountability } from '../../Types/Incident';
import { appColors } from '../../Config/Theme';
import { getProgressBarColor } from '../../Utils/incidentDashboardState';

interface AccountabilityByViewPanelProps {
  views: ViewAccountability[];
  onViewAll?: () => void;
}

const AccountabilityByViewPanel: React.FC<AccountabilityByViewPanelProps> = ({ views, onViewAll }) => (
  <Box
    sx={{
      bgcolor: appColors.surface.card,
      borderRadius: '12px',
      border: `1px solid ${appColors.border.default}`,
      p: 2.5,
      flex: '1 1 0',
      minWidth: { xs: 0, sm: 280 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ViewIcon sx={{ fontSize: 18, color: appColors.primary.main }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Accountability by View
        </Typography>
      </Box>
      {onViewAll && (
        <Link component="button" variant="caption" onClick={onViewAll} sx={{ fontWeight: 600, color: appColors.primary.main }}>
          View All →
        </Link>
      )}
    </Box>

    {/* Table header */}
    <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 0.5 }}>
      {['View', 'Total', 'Accounted', 'Not Accounted', 'Not Checked', 'Progress'].map((h) => (
        <Typography
          key={h}
          variant="caption"
          sx={{ color: appColors.text.placeholder, fontWeight: 600, flex: h === 'View' ? '2 1 0' : '1 1 0', textAlign: h === 'View' ? 'left' : 'center' }}
        >
          {h}
        </Typography>
      ))}
    </Box>

    {views.length === 0 && (
      <Typography variant="body2" sx={{ color: appColors.text.disabled, py: 2, textAlign: 'center' }}>
        Roster views are available dynamically. Track progress by full roster, floor, department, person type, or last-name range.
      </Typography>
    )}

    {views.map((v, idx) => (
      <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1, px: 0.5, borderTop: `1px solid ${appColors.border.light}` }}>
        <Box sx={{ flex: '2 1 0' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, lineHeight: 1.3 }}>
            {v.viewName}
          </Typography>
          <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
            Headcounter: {v.headcounterName}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ flex: '1 1 0', textAlign: 'center', fontWeight: 600, color: appColors.text.heading }}>
          {v.total}
        </Typography>
        <Typography variant="body2" sx={{ flex: '1 1 0', textAlign: 'center', fontWeight: 600, color: appColors.text.heading }}>
          {v.accounted}
        </Typography>
        <Typography variant="body2" sx={{ flex: '1 1 0', textAlign: 'center', fontWeight: 600, color: appColors.status.error }}>
          {v.notAccounted}
        </Typography>
        <Typography variant="body2" sx={{ flex: '1 1 0', textAlign: 'center', fontWeight: 600, color: appColors.text.caption }}>
          {v.notChecked}
        </Typography>
        <Box sx={{ flex: '1 1 0', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={v.percentComplete}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              bgcolor: appColors.border.default,
              '& .MuiLinearProgress-bar': { bgcolor: getProgressBarColor('inProgress', v.percentComplete), borderRadius: 3 },
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 600, color: appColors.text.heading, minWidth: 32, textAlign: 'right' }}>
            {Math.round(v.percentComplete)}%
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

export default AccountabilityByViewPanel;
